import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { useForm } from '@domain-group/react-hooks'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import Activity from '@components/EntityActivity/Activity'
import defaultRequestOptions from '@components/EntityActivity/defaultRequestOptions'

import useCampaignItem from '@hooks/useCampaignItem'
import useOrder from '@hooks/useOrder'

import defaultState from '@models/reconciliationItem'

const MODAL_KEY = 'EditReconciliationItemModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateReconciliationItem: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditReconciliationItemModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateReconciliationItem } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { item: initItem } = modalPayload
  const { itemType } = initItem

  const {
    callbacks: { updateOrder: updateOrderFn },
    order,
    updating: orderUpdating,
  } = useOrder(initItem)
  const { id: orderId, title: orderTitle, total } = order || {}

  const {
    campaignItem,
    callbacks: { updateCampaignItem: updateCampaignItemFn }, updating: campaignUpdating,
  } = useCampaignItem(initItem)
  const { id: campaignItemId, price_override: expectedTotal, title: campaignTitle } = campaignItem || {}

  const itemFormPayload = useForm(
    defaultState,
    {
      entity: initItem,
      validateOn: 'change',
    },
  )

  const {
    entityState,
    entityState: {
      invoice_date,
      invoice_notes,
      invoice_reference,
      invoice_total,
      paid_by,
    },
    handlers,
  } = itemFormPayload

  const updateReconciliationItemPayload = {
    callbacks: {
      action: itemType === 'order' ? updateOrderFn : updateCampaignItemFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Reconciliation Item Updated',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Item" />
      <SidebarModal.Content>
        <Form>
          <Form.Field label="Item Title">
            <input disabled type="text" value={campaignTitle || orderTitle} />
          </Form.Field>

          <Form.Row marginTop="large">
            <Form.Field label="Order Number">
              <input disabled type="text" value={orderId || campaignItemId} />
            </Form.Field>

            <Form.Field label="Invoice #">
              <input name="invoice_reference" type="text" value={invoice_reference} {...handlers} />
            </Form.Field>
          </Form.Row>

          <Form.Row marginTop="large">
            <Form.Field label="Expected Total">
              <input disabled type="number" value={total || expectedTotal} />
            </Form.Field>

            <Form.Field label="Actual Total">
              <input name="invoice_total" type="number" value={invoice_total} {...handlers} />
            </Form.Field>
          </Form.Row>

          <Form.Field label="Paid by" marginTop="large">
            {/*
             // @ts-expect-error TS2322 */}
            <select name="paid_by" value={paid_by} {...handlers}>
              <option value="">Not Selected...</option>
              <option value="agency">Paid by Agency</option>
              <option value="agent">Paid by Agent</option>
              <option value="commission">Paid from Commission</option>
              <option value="vendor">Paid by Vendor</option>
            </select>
          </Form.Field>

          <Form.Field label="Invoice Date" marginTop="large">
            <input name="invoice_date" type="date" value={invoice_date || ''} {...handlers} />
          </Form.Field>

          <Form.Field label="Comments" marginTop="large">
            <input name="invoice_notes" type="text" value={invoice_notes} {...handlers} />
          </Form.Field>
        </Form>

        {!!(orderId || campaignItemId) && (
          <>
            <Text fontSize="small" fontWeight="bold" margin="large">
              Reconciliation Activity
            </Text>

            <Activity
              boxProps={{ marginTop: 'medium' }}
              filters={{ limit: 20, trackable_only: true }}
              requestOptions={defaultRequestOptions.activity}
              subjectId={orderId || campaignItemId}
              subjectType={itemType === 'order' ? 'Order' : 'CampaignItem'}
            />
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={orderUpdating || campaignUpdating}
          onClick={() => updateReconciliationItem(updateReconciliationItemPayload)}
          size="large"
        >
          Save Item
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditReconciliationItemModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
